import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { useAuthenticatedUser } from '@/components/providers/AuthenticatedUserProvider';
import { useFeatureFlags } from '@/components/providers/FeatureFlagsProvider';
import { type Project, projectQueries } from '@/lib/queries/project';

import { InviteUserDialog } from './InviteUserDialog';
import { InviteUserForm } from './InviteUserForm';
import { MembersTable } from './MembersTable';
import { TogglePrImpact } from './TogglePrImpact';

interface Props {
  mainProject: Project;
}

export const ProjectManagement = ({ mainProject }: Props): JSX.Element => {
  const [email, setEmail] = useState<string>();
  const { user } = useAuthenticatedUser();
  const { FeaturePRImpactAnalysis } = useFeatureFlags();

  const { data: projectDetails } = useQuery(
    projectQueries.detail(mainProject.id),
  );

  const isAdmin = useMemo(() => {
    return projectDetails?.members.some(
      member => member.role === 'admin' && member.email === user.email,
    );
  }, [projectDetails, user.email]);

  return (
    <section>
      <div>
        <h1 className="text-xl mb-2">
          <FormattedMessage id="configuration.projectManagement.title" />
        </h1>
      </div>
      <InviteUserForm setEmail={setEmail} isAdmin={isAdmin} />
      {email !== undefined ? (
        <InviteUserDialog
          email={email}
          mainProject={mainProject}
          closeDialog={() => setEmail(undefined)}
        />
      ) : (
        <></>
      )}
      {projectDetails !== undefined && (
        <>
          <MembersTable
            members={projectDetails.members}
            isAdmin={isAdmin}
            mainProject={mainProject}
          />
          {FeaturePRImpactAnalysis === true && (
            <TogglePrImpact projectDetails={projectDetails} />
          )}
        </>
      )}
    </section>
  );
};
